import type { ComponentProps, ReactNode, Ref } from 'react';
import { Link, type LinkProps } from 'react-router';
import { cx } from '../../lib/cx';
import { buttonClass, type ButtonVariant } from './buttonClass';
import { Spinner } from './Spinner';

export type { ButtonVariant };

interface CommonProps {
  variant?: ButtonVariant;
  size?: 'md' | 'sm' | 'lg';
  loading?: boolean;
  iconStart?: ReactNode;
  fullWidth?: boolean;
  className?: string;
  children?: ReactNode;
}

type ButtonProps = CommonProps &
  Omit<ComponentProps<'button'>, 'children'> & { ref?: Ref<HTMLButtonElement> };

/** DESIGN 5.1. md = 48px, sm = 44px, lg = 52px (sticky bottom bar). */
export function Button({
  variant,
  size,
  loading = false,
  iconStart,
  fullWidth,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={buttonClass({ variant, size, fullWidth, className })}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner /> : iconStart}
      {children}
    </button>
  );
}

type ButtonLinkProps = CommonProps & Omit<LinkProps, 'className' | 'children'>;

/** Same look, rendered as a router link. */
export function ButtonLink({
  variant,
  size,
  iconStart,
  fullWidth,
  className,
  children,
  ...rest
}: ButtonLinkProps) {
  return (
    <Link className={buttonClass({ variant, size, fullWidth, className })} {...rest}>
      {iconStart}
      {children}
    </Link>
  );
}

type IconButtonProps = Omit<ComponentProps<'button'>, 'aria-label'> & {
  label: string;
  ref?: Ref<HTMLButtonElement>;
};

/** 44px round icon button (`.iconbtn`). */
export function IconButton({
  label,
  className,
  children,
  type = 'button',
  ...rest
}: IconButtonProps) {
  return (
    <button type={type} aria-label={label} className={cx('iconbtn', className)} {...rest}>
      {children}
    </button>
  );
}

export function IconLink({
  label,
  className,
  children,
  ...rest
}: Omit<LinkProps, 'aria-label'> & { label: string }) {
  return (
    <Link aria-label={label} className={cx('iconbtn', className)} {...rest}>
      {children}
    </Link>
  );
}
